import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";

// ThatOpen / IFC.js
import * as OBF from "@thatopen/components-front";
import * as THREE from "three";

import { DrawVectorTool, PlanOrigin as Origin } from "@treegram/tools-draw-vector";
import { CameraTool } from "@treegram/tools-camera";
import { ImgLoader } from "@treegram/loaders-img";
import ViewerError from "@/components/viewer-error";
import ViewerLoading from "@/components/viewer-loading";

import useProjectFilePlanContext from "../context/use-project-file-plan-context.ts";
import { highContrastColors } from "@/utils/colors.ts";
import generateSequenceName from "@/utils/generate-sequence-name.ts";

import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";
import PlanOrigin from "@/pages/takeoff/context/project-organisation/models/plan-origin.ts";
import useViewer, { createViewerTool, ViewerLoader, ViewerTool } from "@/hooks/viewer/use-viewer.ts";

const ViewerLeft: React.FC = () => {
    const { isOverlayActive, urlFileLeft: urlFilePlan, fileLeft: file, planFileDataLeft: planFileData, fileDataRight, isDrawingLeft: isDrawing, setIsDrawingLeft: setIsDrawing, dispatchLeft: dispatch } = useProjectFilePlanContext();

    const containerRef = useRef<HTMLDivElement>(null);
    const originsInitializedRef = useRef(false);

    /* --------- TOOLS CONFIG --------- */
    const tools = useMemo<ViewerTool[]>(() => [
        createViewerTool({
            key: "cameraTool",
            factory: (ctx) => new CameraTool(ctx.viewer),
            setup: (tool) => {
                tool.setNavigationMode("Plan");
            },
        }),
        createViewerTool({
            key: "drawVectorTool",
            factory: (ctx) => new DrawVectorTool(ctx.viewer),
        }),
    ], []);

    /* --------- LOADERS CONFIG --------- */
    const planPdfLoader = useMemo<ViewerLoader<unknown, ImgLoader, typeof file>>(() => ({
        key: "plan-pdf-loader",
        factory: (ctx) => new ImgLoader(ctx.viewer.getComponents(), ctx.world),
        itemsToLoad: file != null ? [file] : undefined,
        fetchUrl: () => urlFilePlan,
        name: (item) => item?.name ?? "plan",
        getOptions: () => {
            let loaderOptions = {};
            if (planFileData.content instanceof KnownScaleContent) {
                loaderOptions = {
                    format: planFileData.content.format,
                    orientation: planFileData.content.orientation,
                    scale: planFileData.content.scale,
                };
            } else if (planFileData.content instanceof UserDefinedContent) {
                loaderOptions = { definedScale: planFileData.content.scale };
            }
            return { useCoordinates: false, loaderOptions };
        },
        onItemLoaded: async (ctx, loader) => {
            const dimensionsCanvas = loader.getDimensionsCanvas();
            if (dimensionsCanvas) {
                await ctx.viewer?.getCamera().controls.setLookAt(0, Math.max(dimensionsCanvas.height, dimensionsCanvas.width) * 0.9, 0, 0, 0, 0);
            }
        },
        onError: (_ctx, _loader, _item, error) => {
            toast.error(error.message, { autoClose: false });
        },
        enabled: urlFilePlan != null && file != null,
    }), [file, urlFilePlan, planFileData.content]);

    const viewerLoaders = useMemo<ViewerLoader[]>(
        () => [planPdfLoader],
        [planPdfLoader]
    );

    const { isLoading, loadingProgress, getTool, error, retry } = useViewer({
        containerRef,
        forceResize: true,
        viewerLoaders,
        tools,
    });


    // Gestion lorsque le viewer a fini de dessiner un trait
    const drawnEndedCallback = useCallback(async (e: Origin) => {
        const drawVectorTool = getTool(DrawVectorTool);
        if (drawVectorTool == null) return;

        const color = drawVectorTool.getColor();

        await drawVectorTool.deactivate();
        drawVectorTool.onEndedCreation.remove(drawnEndedCallback);
        setIsDrawing(false);

        const origin = new PlanOrigin();
        origin.setName(generateSequenceName('Origine', planFileData.origins.length));
        origin.setColor(`#${color.getHexString()}`);
        origin.setPosition(e.position);
        origin.setDirection(e.direction);
        origin.setIsActive(true);
        origin.relativeOrigineId = fileDataRight.origins.find(o => o.isActive)?.id;

        dispatch({
            type: 'add-origin',
            origin
        });
    }, [getTool, planFileData.origins.length, fileDataRight.origins, setIsDrawing, dispatch]);

    // Gestion passage en mode dessin
    useEffect(() => {
        let cancelled = false;
        const drawVectorTool = getTool(DrawVectorTool);

        if (!isDrawing || !drawVectorTool) {
            return () => {
                cancelled = true;
            };
        }

        const run = async () => {
            if (!drawVectorTool) return;

            await drawVectorTool.activate();
            if (cancelled) return;

            const colorHex = highContrastColors[planFileData.origins.length % highContrastColors.length];
            drawVectorTool.setColor(new THREE.Color(colorHex));
            drawVectorTool.draw();

            drawVectorTool.onEndedCreation.add(drawnEndedCallback);
        };

        run();

        // Cleanup en cas de changement de state pendant l’attente
        return () => {
            cancelled = true;
            drawVectorTool?.onEndedCreation.remove(drawnEndedCallback);
        };

    }, [isDrawing, getTool, planFileData.origins.length, drawnEndedCallback]);

    // Affichage des origines sur le viewer
    useEffect(() => {
        const drawVectorTool = getTool(DrawVectorTool);
        if (error) {
            originsInitializedRef.current = false;
            return;
        }
        if (!isOverlayActive || isLoading || !drawVectorTool) return;
        if (originsInitializedRef.current) return;

        drawVectorTool.clear();

        const vertorLength = 3;

        planFileData.origins.forEach(origin => {
            drawVectorTool.setColor(new THREE.Color(origin.color));
            drawVectorTool.addMeasure(new OBF.Line(
                new THREE.Vector3(origin.position.x, 0, -origin.position.y),
                new THREE.Vector3(origin.position.x + vertorLength * origin.direction.x, 0, -origin.position.y - vertorLength * origin.direction.y)
            ));
        });

        originsInitializedRef.current = true;
    }, [isOverlayActive, isLoading, getTool, planFileData.origins, error]);

    useEffect(() => {
        originsInitializedRef.current = false;
    }, [urlFilePlan]);

    return <div className="relative w-full h-full" aria-busy={isLoading && !error}>
        <div ref={containerRef} className="w-full h-full" />
        {isLoading && !error && <ViewerLoading progress={loadingProgress} />}
        <ViewerError error={error} retry={retry} />
    </div>;
}

export default ViewerLeft;
